import { RewardCountdown } from "@/pages/UtilitiesPage";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, LogOut, Receipt, User, Check, Loader2, Coins, Ticket } from "lucide-react";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NotificationSettingsCard } from "@/components/NotificationSettingsCard";

export default function Profile() {
  const { user, ready, isAuthed, isSubscribed, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [history, setHistory] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [points, setPoints] = useState(null);
  const [ledger, setLedger] = useState([]);

  useEffect(() => {
    if (ready && !isAuthed) { window.dispatchEvent(new Event("open-auth")); nav("/"); }
  }, [ready, isAuthed, nav]);

  useEffect(() => {
    if (isAuthed) {
      setName(user?.displayName || "");
      setEmail(user?.email || "");
      endpoints.history().then((r) => setHistory(r.data)).catch(() => {});
      endpoints.pointsBalance().then((r) => setPoints(r.data.points)).catch(() => {});
      endpoints.pointsHistory().then((r) => setLedger(r.data || [])).catch(() => {});
    }
  }, [isAuthed, user]);

  if (!ready || !isAuthed) return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;

  const save = async () => {
    setSaving(true);
    try { await endpoints.updateMe({ displayName: name, email }); await refresh(); toast.success("Profile updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); } finally { setSaving(false); }
  };

  const exp = user?.subscriptionExpiresAt ? new Date(user.subscriptionExpiresAt).toLocaleDateString() : null;

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-10">
      <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-8">My Account</h1>

      <div className="grid md:grid-cols-2 gap-6">
        <div className={`rounded-2xl p-6 border ${isSubscribed ? "border-[#2563EB]/40 bg-gradient-to-br from-[#0B1220] to-[#0A0C10]" : "border-[#273042] bg-[#121620]"}`}>
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider" style={{ color: isSubscribed ? "#2563EB" : "#94A3B8" }}>
            <Crown className="w-4 h-4" /> {isSubscribed ? (user?.tier !== "free" ? `${user?.tier} member` : "Utilities reward") : "Free account"}
          </div>
          <div className="mt-3 text-white text-2xl font-extrabold capitalize">{isSubscribed ? (user?.tier !== "free" ? `${user?.tier} plan` : "Free access active") : "No active plan"}</div>
          {exp && <div className="text-sm text-slate-400 mt-1">Renews / expires {exp}</div>}
          {user?.utilityAccessUntil && <RewardCountdown until={user.utilityAccessUntil} className="mt-3" />}
          {!isSubscribed && <button onClick={() => nav("/pricing")} data-testid="profile-upgrade-btn" className="mt-5 h-11 px-6 rounded-full bg-[#E11D2A] text-white font-bold btn-glow">Upgrade to Premium</button>}
        </div>

        <div className="rounded-2xl p-6 border border-[#273042] bg-[#121620]">
          <div className="flex items-center gap-2 text-white font-bold"><User className="w-5 h-5 text-[#E11D2A]" /> Details</div>
          <div className="mt-4 text-sm text-slate-400">Phone</div>
          <div className="text-white font-mono">{user?.phone || user?.email}</div>
          <label className="block mt-4 text-sm text-slate-400">Display name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input"
            className="mt-1.5 w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white outline-none focus:border-[#E11D2A]/60" />
          <label className="block mt-4 text-sm text-slate-400">Email <span className="text-slate-500">(optional)</span></label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="profile-email-input" type="email"
            placeholder="you@example.com"
            className="mt-1.5 w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-4 text-white outline-none focus:border-[#E11D2A]/60" />
          <div className="flex gap-2 mt-4">
            <button onClick={save} disabled={saving} data-testid="profile-save-btn" className="h-10 px-5 rounded-full bg-[#E11D2A] text-white text-sm font-bold flex items-center gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
            </button>
            <button onClick={() => { logout(); nav("/"); }} data-testid="profile-logout-btn" className="h-10 px-5 rounded-full bg-white/5 border border-[#273042] text-red-400 text-sm font-semibold flex items-center gap-1.5"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-[#273042] bg-[#121620] overflow-hidden">
        <div className="p-5 border-b border-[#273042] flex items-center gap-2 text-white font-bold"><Receipt className="w-5 h-5 text-[#E11D2A]" /> Billing history</div>
        {history.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No payments yet.</div>
        ) : (
          <div className="divide-y divide-[#273042]">
            {history.map((h) => (
              <div key={h.id} data-testid={`billing-row-${h.id}`} className="p-4 flex items-center justify-between">
                <div>
                  <div className="text-white text-sm font-semibold">{h.planLabel || h.plan}</div>
                  <div className="text-xs text-slate-400">{new Date(h.createdAt).toLocaleString()} · {h.method}</div>
                </div>
                <div className="text-right">
                  <div className="text-white font-semibold">{Number(h.amount).toLocaleString()} {h.currency}</div>
                  <div className={`text-xs font-bold uppercase ${h.status === "paid" ? "text-[#22C55E]" : "text-slate-400"}`}>{h.status}</div>
                  {h.status === "paid" && h.receiptUrl && (
                    <a href={h.receiptUrl} target="_blank" rel="noopener noreferrer" data-testid={`receipt-link-${h.id}`}
                      className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#E11D2A] hover:underline">
                      <Receipt className="w-3.5 h-3.5" /> Receipt
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-[#273042] bg-[#121620] p-5" data-testid="my-tickets-card">
          <div className="flex items-center gap-2 text-white font-bold"><Ticket className="w-5 h-5 text-[#E11D2A]" /> My Tickets</div>
          <p className="text-slate-400 text-sm mt-1">View, download or show the QR for every ticket you bought.</p>
          <button onClick={() => nav("/my-tickets")} data-testid="go-my-tickets-btn" className="mt-4 h-10 px-5 rounded-full bg-[#E11D2A] text-white text-sm font-bold">Open My Tickets</button>
        </div>

        <div className="rounded-2xl border border-[#F5B301]/30 bg-[#F5B301]/5 p-5" data-testid="points-statement">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white font-bold"><Coins className="w-5 h-5 text-[#F5B301]" /> Points & Voting</div>
            <div className="text-[#F5B301] font-extrabold" data-testid="points-balance">{points == null ? "…" : `${points.toLocaleString()} pts`}</div>
          </div>
          {ledger.length === 0 ? (
            <p className="text-slate-400 text-sm mt-3">No points activity yet. Buy points to vote in paid campaigns.</p>
          ) : (
            <div className="mt-3 max-h-56 overflow-y-auto divide-y divide-[#273042]/60">
              {ledger.slice(0, 20).map((l) => (
                <div key={l.id} className="py-2 flex items-center justify-between text-sm" data-testid={`points-row-${l.id}`}>
                  <div className="min-w-0"><div className="text-slate-200 truncate capitalize">{(l.reason || l.type || "activity").replace(/_/g, " ")}</div>
                    <div className="text-[11px] text-slate-500">{l.createdAt ? new Date(l.createdAt).toLocaleString() : ""}</div></div>
                  <div className={`font-bold ${Number(l.delta ?? l.points ?? 0) < 0 ? "text-red-400" : "text-emerald-400"}`}>{Number(l.delta ?? l.points ?? 0) > 0 ? "+" : ""}{Number(l.delta ?? l.points ?? 0).toLocaleString()} pts</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <NotificationSettingsCard />
    </div>
  );
}
